import { useCallback, useEffect } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import type { FetchResult, NormalizedCacheObject } from "@apollo/client";
import { useMutation } from "@apollo/client";
import type { ApolloCache } from "@apollo/client/cache";
import type { OperationVariables } from "@apollo/client/core";
import type { MutationUpdaterFunction } from "@apollo/client/core/types";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import type { Error } from "gql";
import type { DocumentNode } from "graphql";
import { isNil } from "ramda";

export const useSubmitMutation = <
  TFormData extends Record<string, unknown>,
  TData = unknown,
  TVariables = OperationVariables,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: {
    varSetter: (values: TFormData) => TVariables;
    dataGetter: (
      response?: FetchResult<TData>
    ) =>
      | (TData[keyof Omit<TData, "__typename">] & { errors?: Array<Maybe<Error>> | undefined | null })
      | undefined
      | null;
    onSave?: string | ((data: TData[keyof Omit<TData, "__typename">]) => void);
    update?: MutationUpdaterFunction<TData, TVariables, unknown, ApolloCache<NormalizedCacheObject>>;
  }
): ((values: TFormData) => Promise<void | TData[keyof TData] | undefined | null>) => {
  const { varSetter, dataGetter, update, onSave } = options;
  const navigate = useNavigate();
  const { showBoundary: onError } = useErrorBoundary();
  const [executeUpdate, { data }] = useMutation(mutation, { onError, update });
  const responseData = dataGetter({ data });
  useEffect(() => {
    if (!isNil(responseData)) {
      if (!isNil(responseData.errors)) {
        onError("Failed to save data");
      } else {
        if (typeof onSave === "string") {
          navigate(onSave, { replace: true });
        } else if (!isNil(onSave)) {
          onSave(responseData);
        }
      }
    }
  }, [navigate, onError, onSave, responseData]);
  return useCallback(
    (values: TFormData): Promise<void | TData[keyof TData] | undefined | null> => {
      const variables = varSetter(values);
      return executeUpdate({ variables })
        .then((res) => dataGetter(res))
        .catch((error) => {
          onError(error);
        });
    },
    [dataGetter, executeUpdate, onError, varSetter]
  );
};
